// export default NearComplete;
import React, { useContext, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FarmerOverlay from "../Components/FarmerOverlay";
import DataContext from "../Context/DAtaContext";
// icons imports
import { FaCheckCircle, FaSearch, FaInbox } from "react-icons/fa";

/**
 * Circular progress ring.
 * Renders the percentage centered inside an animated SVG ring.
 * Colors are driven by the same palette already used across the page
 * (green / yellow / orange), just expressed as a stroke instead of a bar.
 */
const CircularProgress = ({ percentage, size = 56, strokeWidth = 5 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(percentage, 100);
  const offset = circumference - (clamped / 100) * circumference;

  // Same thresholds as the rest of the component, just mapped to a ring color.
  const ringColorClass =
    percentage >= 95
      ? "text-green-500"
      : percentage >= 85
        ? "text-yellow-500"
        : "text-orange-500";

  const labelColorClass =
    percentage >= 95
      ? "text-green-600"
      : percentage >= 85
        ? "text-yellow-600"
        : "text-orange-600";

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${percentage}% paid`}
    >
      <svg width={size} height={size} className="-rotate-90">
        {/* track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* progress */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          className={ringColorClass}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`font-bold ${labelColorClass}`}
          style={{ fontSize: size <= 44 ? "9px" : "11px" }}
        >
          {percentage}%
        </span>
      </div>
    </div>
  );
};

const NearComplete = ({ users }) => {
  const { vegetableBundle, Payment, farmerbundle } = useContext(DataContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [open, setOpen] = useState(false);
  const [show1] = useState(true);

  // Calculate farmers with payment completion between 75% and 99%
  const nearCompleteFarmers = useMemo(() => {
    if (!users || !farmerbundle || !vegetableBundle || !Payment) return [];

    return users
      .map((user, index) => {
        // Get farmer bundles
        const userBundles = farmerbundle?.filter(
          (dt) => dt?.farmer_id === user?.uuid,
        );

        // Calculate total cost for this farmer
        const totalCost =
          userBundles?.reduce((sum, bundle) => {
            const vegBundle = vegetableBundle?.find(
              (vb) => vb?.id === bundle?.bundle_id,
            );
            const price = vegBundle?.price || 0;
            return sum + bundle?.amount * price;
          }, 0) || 0;

        // Get ALL payment amounts for this farmer and sum them
        const userPayments = Payment?.filter(
          (dt) => dt?.farmer_id === user?.uuid,
        );

        // Sum all payments for this farmer (handling multiple payments)
        const totalPaidAmount =
          userPayments?.reduce(
            (sum, payment) => sum + (payment?.amount || 0),
            0,
          ) || 0;

        // Calculate percentage with proper handling of edge cases
        let percentage = 0;
        if (totalCost > 0) {
          percentage = Math.min((totalPaidAmount / totalCost) * 100, 100);
        } else if (totalPaidAmount > 0) {
          // If there's payment but no cost calculated, consider it as overpaid (100%)
          percentage = 100;
        }

        return {
          ...user,
          totalCost,
          paidAmount: totalPaidAmount,
          percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal place
          bundles: userBundles,
          paymentsCount: userPayments?.length || 0,
          originalIndex: index,
        };
      })
      .filter((farmer) => {
        // Filter for 75% to 99% (exclusive of 100%)
        return farmer.percentage >= 75 && farmer.percentage < 100;
      })
      .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending
  }, [users, farmerbundle, vegetableBundle, Payment]);

  // handle Farmer selection overlay
  const handleFarmer = (id) => {
    setFarmerId(id);
    setOpen(!open);
  };

  // Filter near complete farmers based on the search term
  const filteredFarmers = nearCompleteFarmers?.filter(
    (farmer) =>
      farmer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farmer.id?.toString().includes(searchTerm),
  );

  // Get percentage color
  const getPercentageColor = (percentage) => {
    if (percentage >= 95) return "text-green-600 font-bold";
    if (percentage >= 85) return "text-yellow-600 font-semibold";
    if (percentage >= 75) return "text-orange-600 font-semibold";
    return "text-gray-600";
  };

  // Get status badge for farmers in 75%-99% range
  const getStatusBadge = (percentage) => {
    if (percentage >= 95) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full whitespace-nowrap">
          <FaCheckCircle className="w-3 h-3 mr-1" />
          Almost There
        </span>
      );
    }
    if (percentage >= 85) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full whitespace-nowrap">
          Very Close
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-orange-800 bg-orange-100 rounded-full whitespace-nowrap">
        Near Complete
      </span>
    );
  };

  return (
    <div className="relative">
      {show1 ? (
        <>
          {/* Overlay table input */}
          <FarmerOverlay FarmerId={farmerId} open={open} setOpen={setOpen} />

          {/* Card wrapper */}
          <div className="">
            {/* Header */}
            <div className="py-5 border-b border-gray-100 dark:border-gray-800">
              <div className="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-start">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    Near Complete Investors
                  </h2>
                  <p className="mt-0.5   text-sm text-gray-600 dark:text-gray-400">
                    Farmers with 75% - 99% payment completion (
                    {filteredFarmers?.length} farmers)
                  </p>
                </div>
                <div className="flex items-center self-start gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-950/40 sm:self-auto">
                  <div className="w-2.5 h-2.5 bg-orange-500 rounded-full shrink-0"></div>
                  <span className="text-xs font-medium text-orange-700 whitespace-nowrap dark:text-orange-300">
                    Payment Range: 75% - 99%
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="px-3 py-2.5 bg-white rounded-lg dark:bg-gray-900">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Total farmers processed
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {users?.length}
                  </p>
                </div>
                <div className="px-3 py-2.5 bg-white rounded-lg dark:bg-gray-900">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Farmers in 75%-99% range
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {nearCompleteFarmers?.length}
                  </p>
                </div>
              </div>

              {/* Search input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search near complete farmers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2.5 pl-10 pr-4 text-sm border border-gray-200 rounded-lg outline-none sm:w-72 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FaSearch className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {filteredFarmers?.length > 0 ? (
              <>
                {/* ---------- Mobile: card list (below sm) ---------- */}
                <div className="block space-y-3 overflow-y-auto sm:hidden max-h-96">
                  <AnimatePresence initial={false}>
                    {filteredFarmers?.map((farmer, index) => (
                      <motion.div
                        key={farmer.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        onClick={() => handleFarmer(farmer.id)}
                        className="p-3 bg-white border border-gray-200 rounded-lg cursor-pointer dark:bg-slate-900 dark:border-gray-700 active:bg-green-50 dark:active:bg-gray-700"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center min-w-0 gap-3">
                            <CircularProgress
                              percentage={farmer.percentage}
                              size={48}
                              strokeWidth={4.5}
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
                                {farmer?.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                ID: {farmer?.id}
                                {farmer?.prefered_bundle
                                  ? ` · ${farmer.prefered_bundle}`
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-green-500 rounded-full shrink-0 dark:bg-gray-700">
                            {index + 1}
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 dark:border-gray-800">
                          <div className="text-xs">
                            <span className="text-gray-500 dark:text-gray-400">
                              Paid:{" "}
                            </span>
                            <span className="font-medium text-green-600">
                              {farmer.paidAmount?.toLocaleString()}
                            </span>
                            <span className="mx-1 text-gray-300 dark:text-gray-600">
                              /
                            </span>
                            <span className="text-gray-600 dark:text-gray-300">
                              {farmer.totalCost?.toLocaleString()}
                            </span>
                          </div>
                          {getStatusBadge(farmer.percentage)}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* ---------- Desktop / tablet: table (sm and up) ---------- */}
                <div className="relative hidden overflow-y-auto sm:block h-[600px]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20 text-white bg-green-600 shadow">
                      <tr>
                        <th className="hidden p-3 text-xs text-left xl:table-cell md:text-sm">
                          #
                        </th>
                        <th className="p-3 text-xs text-left md:text-sm">ID</th>
                        <th className="p-3 text-xs text-left md:text-sm">
                          Name
                        </th>
                        <th className="hidden p-3 text-xs text-left xl:table-cell md:text-sm">
                          Package
                        </th>
                        <th className="p-3 text-xs text-center md:text-sm">
                          Amount Paid
                        </th>
                        <th className="p-3 text-xs text-center md:text-sm">
                          Total Cost
                        </th>
                        <th className="p-3 text-xs text-center md:text-sm">
                          Progress
                        </th>
                        <th className="hidden p-3 text-xs text-center xl:table-cell md:text-sm">
                          Status
                        </th>
                      </tr>
                    </thead>

                    <motion.tbody>
                      {filteredFarmers?.map((farmer, index) => {
                        return (
                          <motion.tr
                            key={farmer.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="transition duration-150 ease-in-out bg-white border-b dark:border-b-gray-700 dark:text-gray-300 dark:bg-slate-900 dark:hover:bg-gray-700 hover:bg-green-50"
                          >
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="hidden p-3 text-xs cursor-pointer xl:table-cell md:text-sm"
                            >
                              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-green-500 rounded-full dark:bg-gray-700">
                                {index + 1}
                              </span>
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="p-3 text-xs cursor-pointer md:text-sm"
                            >
                              {farmer?.id}
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="p-3 text-xs font-medium cursor-pointer md:text-sm"
                            >
                              {farmer?.name}
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="hidden p-3 text-xs cursor-pointer xl:table-cell md:text-sm"
                            >
                              {farmer?.prefered_bundle}
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="p-3 text-xs font-medium text-center text-green-600 cursor-pointer md:text-sm"
                            >
                              {farmer.paidAmount?.toLocaleString()}
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="p-3 text-xs text-center cursor-pointer md:text-sm"
                            >
                              {farmer.totalCost?.toLocaleString()}
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="p-3 text-xs text-center cursor-pointer md:text-sm"
                            >
                              <div className="flex items-center justify-center">
                                <CircularProgress
                                  percentage={farmer.percentage}
                                  size={52}
                                  strokeWidth={5}
                                />
                              </div>
                            </td>
                            <td
                              onClick={() => handleFarmer(farmer.id)}
                              className="hidden p-3 text-xs text-center cursor-pointer xl:table-cell md:text-sm"
                            >
                              {getStatusBadge(farmer.percentage)}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </motion.tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 text-gray-400 bg-gray-100 rounded-full dark:bg-gray-800 dark:text-gray-500">
                  <FaInbox className="w-6 h-6" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
                  No Near Complete Farmers
                </h3>
                <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm
                    ? "No farmers matching your search are in the 75% - 99% payment completion range."
                    : "Currently no farmers are in the 75% - 99% payment completion range."}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        ""
      )}
    </div>
  );
};

export default NearComplete;
